import { useLazyQuery } from '@apollo/client/react';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ScrollBox } from '~/components/blocks';
import { GetDisclosedMedicineNotesDocument } from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { useSubscriptions } from '~/hooks/use-subscriptions';
import { notificationState } from '~/state/layouts/SharedAppShell/atoms';
import { medicineNoteNotificationPanelState } from '~/state/partials/medicine_note_notification_panel/atoms';

export const useFetchDisclosedMedicineNotes = () => {
  const scrollRef = useRef<RefAttributeType<typeof ScrollBox> | null>(null);
  const { medicineNoteCount } = useAtomValue(notificationState);
  const [state, setState] = useAtom(medicineNoteNotificationPanelState);
  const [event] = useSubscriptions('HiccupOnetimeApiCache', ['created']);

  // melmoから調剤薬局を検索し、「開示許可」したお薬手帳
  // または、ワンタイムコードお薬手帳
  const [getDisclosedMedicineNotes, { loading, data }] = useLazyQuery(
    GetDisclosedMedicineNotesDocument,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );

  const [isOnlyUnread, setOnlyUnread] = useState<boolean>(true);
  const handleGetOnlyUnread = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setState((_state) => ({ ..._state, page: 1 }));
      setOnlyUnread(e.currentTarget.checked);
    },
    [setState],
  );

  useEffect(() => {
    if (!data) return;

    const pagesCount = getMe(data)?.organization.disclosedMedicineNotes.pagesCount || 0;
    const nodesCount = getMe(data)?.organization.disclosedMedicineNotes.nodesCount || 0;

    setState((_state) => ({
      ..._state,
      totalPage: pagesCount,
      totalCount: nodesCount,
    }));
    scrollRef.current?.toTop();
  }, [data, setState]);

  useEffect(() => {
    getDisclosedMedicineNotes({
      variables: {
        page: state.page,
        perPage: state.perPage,
        onlyUnread: isOnlyUnread,
      },
    });
  }, [
    getDisclosedMedicineNotes,
    event,
    state.page,
    state.perPage,
    medicineNoteCount,
    isOnlyUnread,
  ]);

  return {
    loading,
    data,
    scrollRef,
    isOnlyUnread,
    handleGetOnlyUnread,
  };
};
